import type { Analytics } from "firebase/analytics";
import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
} from "firebase/analytics";
import app from "@/services/firebase/config";
import { PRO_PRICING } from "@/constants/pro-features";
import type { ProFeatureId } from "@/types/billing.types";

let analyticsInstance: Analytics | null = null;
let initPromise: Promise<Analytics | null> | null = null;

export async function initAnalytics(): Promise<Analytics | null> {
  if (analyticsInstance) {
    return analyticsInstance;
  }

  if (!initPromise) {
    initPromise = (async () => {
      if (typeof window === "undefined") {
        return null;
      }

      const supported = await isSupported();
      if (!supported) {
        return null;
      }

      if (!import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
        return null;
      }

      analyticsInstance = getAnalytics(app);
      return analyticsInstance;
    })();
  }

  return initPromise;
}

async function getAnalyticsOrNull(): Promise<Analytics | null> {
  return initAnalytics();
}

export async function identifyAnalyticsUser(userId: string | null) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  setUserId(analytics, userId);
}

export async function trackPageView(pagePath: string, pageTitle: string) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "page_view", {
    page_path: pagePath,
    page_title: pageTitle,
    page_location: window.location.href,
  });
}

export async function trackLandingCtaClick(ctaId: string) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "select_promotion", {
    promotion_id: ctaId,
    promotion_name: ctaId,
    creative_name: "landing_page",
    location_id: "landing_page",
  });
}

export async function trackSignUp(method: "email" | "google") {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "sign_up", { method });
}

export async function trackLogin(method: "email" | "google") {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "login", { method });
}

export async function trackStartupCreated(source: "onboarding" | "dashboard") {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "startup_created", { source });
}

export async function trackProFunnelView(
  featureId: ProFeatureId | "billing" | "default",
  surface: "hero" | "modal" | "card" | "compact" | "strip",
) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "view_item_list", {
    item_list_id: "pro_upgrade_funnel",
    item_list_name: "BurnChart Pro",
    items: [
      {
        item_id: featureId,
        item_name: featureId,
        item_category: surface,
      },
    ],
  });
}

export async function trackBeginCheckout(
  interval: "month" | "year",
  featureId?: ProFeatureId | "billing" | "default",
) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  const value =
    interval === "year" ? PRO_PRICING.yearlyUsd : PRO_PRICING.monthlyUsd;

  logEvent(analytics, "begin_checkout", {
    currency: "USD",
    value,
    items: [
      {
        item_id: interval === "year" ? "pro_yearly" : "pro_monthly",
        item_name: "BurnChart Pro",
        item_category: featureId ?? "default",
        price: value,
        quantity: 1,
      },
    ],
  });
}

export async function trackPurchase(interval: "month" | "year") {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  const value =
    interval === "year" ? PRO_PRICING.yearlyUsd : PRO_PRICING.monthlyUsd;

  logEvent(analytics, "purchase", {
    currency: "USD",
    value,
    transaction_id: `checkout_success_${Date.now()}`,
    items: [
      {
        item_id: interval === "year" ? "pro_yearly" : "pro_monthly",
        item_name: "BurnChart Pro",
        price: value,
        quantity: 1,
      },
    ],
  });
}

export async function trackCheckoutCanceled(featureId?: string) {
  const analytics = await getAnalyticsOrNull();
  if (!analytics) {
    return;
  }

  logEvent(analytics, "checkout_canceled", {
    feature_id: featureId ?? "unknown",
  });
}
